import { set, uniqBy, get } from 'lodash';
import moment from 'moment';
import { format, add } from 'date-fns';

import appConfig from '../config';
import hotKeys from '../constants/hotKeys';

export const getRoundedNumber = (number) => (
  Number.isInteger(number) ? number : parseFloat(parseFloat(number).toFixed(2))
);

export const adminUrl = () => {
  switch (appConfig.application) {
    case 'bright-prod':
      return 'https://admin.learnwithbright.com';
    case 'bright-dev':
      return process.env.REACT_APP_ADMIN_URL || 'https://admin-dev.learnwithbright.com';
    case 'bright-dev-v2':
      return 'https://admin-dev-v2.learnwithbright.com';
    case 'bright-preprod':
      return 'https://admin-preprod.learnwithbright.com';
    case 'academy-prod':
      return 'https://admin.learnwithbright.academy';
    case 'academy-preprod':
      return 'https://admin-preprod.learnwithbright.academy';
    case 'academy-local':
      return 'http://localhost:3002';
    case 'bright-local':
      return 'http://localhost:3002';
    default:
      return 'https://admin.learnwithbright.com';
  }
};

export const websocketUrl = () => {
  if (appConfig.application === 'bright-local') {
    return 'ws://localhost:5200';
  }

  return `wss://${window.location.host}/bright/api`;
};

export const backendUrl = () => {
  switch (appConfig.application) {
    case 'academy-local':
      return 'http://localhost:5200';
    case 'bright-local':
      return 'http://localhost:5200';
    default:
      // use blank so we use the current domain on the browser
      return '';
  }
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const removeCollissions = (locations) => {
  // take one item among the array
  // check if the range of this particular item is in collission with another item in the array
  // if there is a collission, check which one has the larger length.
  // The smaller one will be discarded.
  const updatedLocations = [...locations];
  for (let i = 0; i < updatedLocations.length; i++) {
    const s1 = updatedLocations[i].offset;
    const s1Length = updatedLocations[i].length;
    const e1 = updatedLocations[i].offset + s1Length;
    for (let j = i + 1; j < updatedLocations.length; j++) {
      const s2 = updatedLocations[j].offset;
      const s2Length = updatedLocations[j].length;
      const e2 = updatedLocations[j].offset + s2Length;
      if (s2 >= s1 && s2 <= e1) {
        // s2 in between the selected location
        if (s1Length >= s2Length) {
          // s2 can be removed
          set(updatedLocations, `[${j}].invalid`, true);
        } else {
          // s1 can be removed
          set(updatedLocations, `[${i}].invalid`, true);
          break;
        }
      } else if (e2 >= s1 && e2 <= e1) {
        if (s1Length >= s2Length) {
          // s2 can be removed
          set(updatedLocations, `[${j}].invalid`, true);
        } else {
          // s1 can be removed
          set(updatedLocations, `[${i}].invalid`, true);
          break;
        }
      } else if (s2 <= e1 && e2 >= s1) {
        if (s1Length >= s2Length) {
          // s2 can be removed
          set(updatedLocations, `[${j}].invalid`, true);
        } else {
          // s1 can be removed
          set(updatedLocations, `[${i}].invalid`, true);
          break;
        }
      }
    }
  }

  return updatedLocations.filter((item) => !item.invalid);
};

export const generateSpeechToTextHighlight = (text, locations = [], isCorrected = false) => {
  const sortedLocations = locations.sort((a, b) => (a.offset < b.offset ? 1 : -1));
  let textWithHighlight = text;
  let validLocations = removeCollissions(sortedLocations);
  validLocations = uniqBy(validLocations, 'offset');

  validLocations.forEach((location) => {
    const start = location.offset;
    const end = location.offset + location.length;

    textWithHighlight = `${textWithHighlight.substring(0, start)}
      <mark style="
        background-color: ${location.color};
        padding: 2px;
      ">
      ${text.substring(start, end)}
      </mark>
      ${textWithHighlight.substring(end)}`;
  });

  return isCorrected ? text : textWithHighlight;
};

export const adaptUrlDomainAccordingToEnvironment = (url = '') => {
  let transformedUrl = '';
  switch (appConfig.application) {
    case 'bright-prod':
      transformedUrl = url.replace(
        /(http|https):\/\/(.*)\.learnwithbright\.com/,
        'https://admin.learnwithbright.com',
      );
      break;
    case 'bright-preprod':
      transformedUrl = url.replace(
        /(http|https):\/\/(.*)\.learnwithbright\.com/,
        'https://admin-preprod.learnwithbright.com',
      );
      break;
    case 'bright-dev':
      transformedUrl = url.replace(
        /(http|https):\/\/(.*)\.learnwithbright\.com/,
        process.env.REACT_APP_ADMIN_URL || 'https://admin-dev.learnwithbright.com',
      );
      break;
    case 'bright-dev-v2':
      transformedUrl = url.replace(
        /(http|https):\/\/(.*)\.learnwithbright\.com/,
        'https://admin-dev-v2.learnwithbright.com',
      );
      break;
    case 'academy-prod':
      transformedUrl = url.replace(
        /(http|https):\/\/(.*)\.learnwithbright\.com/,
        'https://admin.learnwithbright.academy',
      );
      break;
    case 'academy-preprod':
      transformedUrl = url.replace(
        /(http|https):\/\/(.*)\.learnwithbright\.com/,
        'https://admin-preprod.learnwithbright.academy',
      );
      break;
    case 'bright-local': {
      const isPortIncluded = url.includes(':5200');
      transformedUrl = url.replace(
        /(http|https):\/\/(.*)\.learnwithbright\.com/,
        `http://localdev.learnwithbright.com${isPortIncluded ? '' : ':5200'}`,
      );
      break;
    }
    default:
      transformedUrl = url;
      break;
  }
  return transformedUrl;
};

export const getHotkeyCombinations = ({ hotKeyCombo, hotKeyMain, hotKeySecondary }) => {
  let result = '';

  if (hotKeyMain !== undefined) {
    if (hotKeyCombo !== undefined) {
      const hotkeyOptions = hotKeys[hotKeyCombo] || [];
      result = hotkeyOptions.flatMap((combo) => {
        const combinations = [`${combo}+${hotKeyMain}`];
        if (/^[a-z]$/.test(hotKeyMain)) {
          combinations.push(`${combo}+${hotKeyMain.toUpperCase()}`);
        }

        if (hotKeySecondary) {
          combinations.forEach((existingCombo, index, arr) => {
            arr[index] = `${existingCombo}+${hotKeySecondary}`;
          });
        }

        return combinations;
      });
    } else {
      result = [`${hotKeyMain}`];

      if (/^[a-z]$/.test(hotKeyMain)) {
        result.push(hotKeyMain.toUpperCase());
      }

      if (hotKeySecondary) {
        result = result.map((combo) => `${combo}+${hotKeySecondary}`);
      }
    }
  }

  return result;
};

export const convertSecondsToMMSS = (seconds) => {
  if (!seconds) {
    return null;
  }

  const duration = moment.duration(Math.ceil(seconds), 'seconds');
  const minutes = duration.minutes();
  const secondsRemaining = duration.seconds();

  const formattedHours = duration.hours() > 0 ? `${duration.hours()}:` : '';
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  const formattedSeconds = secondsRemaining < 10 ? `0${secondsRemaining}` : secondsRemaining;

  return `${formattedHours}${formattedMinutes}:${formattedSeconds}`;
};

export const convertSecondsToHours = (seconds) => {
  if (!seconds) {
    return null;
  }
  const duration = moment.duration(Math.ceil(seconds), 'seconds');
  const hours = duration.hours();
  // Add Decimal to Hours (0.1 => 6 minutes)
  const hourFraction = Number(((duration.minutes() / 6) * 0.1).toFixed(1));
  return hours + hourFraction;
};

export const setOpacityForRgbColor = (rgbColor, opacity) => {
  // Check if the color is in RGB or RGBA format using regex
  const rgbRegex = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/;
  const rgbaRegex = /^rgba\((\d+),\s*(\d+),\s*(\d+),\s*(\d(\.\d+)?)\)$/;

  if (rgbRegex.test(rgbColor)) {
    const [, r, g, b] = rgbColor.match(rgbRegex);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  if (rgbaRegex.test(rgbColor)) {
    const [, r, g, b] = rgbColor.match(rgbaRegex);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  return rgbColor; // Return the original color if it's not in RGB or RGBA format
};

// Date variable configuration: maps variable patterns to their offset, unit, and format
const DATE_VARIABLE_CONFIG = {
  // Current date/time
  todayDdMonthYyyy: { offset: 0, unit: 'days', format: 'dd MMMM yyyy' },
  todayMonthDdYyyy: { offset: 0, unit: 'days', format: 'MMMM dd, yyyy' },
  todayDdMmYy: { offset: 0, unit: 'days', format: 'dd/MM/yy' },
  todayDdMmYyyy: { offset: 0, unit: 'days', format: 'dd/MM/yyyy' },
  todayMmDdYy: { offset: 0, unit: 'days', format: 'MM/dd/yy' },
  todayMmDdYyyy: { offset: 0, unit: 'days', format: 'MM/dd/yyyy' },

  // Past dates
  yesterdayDdMonthYyyy: { offset: -1, unit: 'days', format: 'dd MMMM yyyy' },
  yesterdayMonthDdYyyy: { offset: -1, unit: 'days', format: 'MMMM dd, yyyy' },
  yesterdayDdMmYy: { offset: -1, unit: 'days', format: 'dd/MM/yy' },
  yesterdayDdMmYyyy: { offset: -1, unit: 'days', format: 'dd/MM/yyyy' },
  yesterdayMmDdYy: { offset: -1, unit: 'days', format: 'MM/dd/yy' },
  yesterdayMmDdYyyy: { offset: -1, unit: 'days', format: 'MM/dd/yyyy' },

  minus1WeekDdMonthYyyy: { offset: -1, unit: 'weeks', format: 'dd MMMM yyyy' },
  minus1WeekMonthDdYyyy: { offset: -1, unit: 'weeks', format: 'MMMM dd, yyyy' },
  minus1WeekDdMmYy: { offset: -1, unit: 'weeks', format: 'dd/MM/yy' },
  minus1WeekDdMmYyyy: { offset: -1, unit: 'weeks', format: 'dd/MM/yyyy' },
  minus1WeekMmDdYy: { offset: -1, unit: 'weeks', format: 'MM/dd/yy' },
  minus1WeekMmDdYyyy: { offset: -1, unit: 'weeks', format: 'MM/dd/yyyy' },

  minus2WeeksDdMonthYyyy: { offset: -2, unit: 'weeks', format: 'dd MMMM yyyy' },
  minus2WeeksMonthDdYyyy: { offset: -2, unit: 'weeks', format: 'MMMM dd, yyyy' },
  minus2WeeksDdMmYy: { offset: -2, unit: 'weeks', format: 'dd/MM/yy' },
  minus2WeeksDdMmYyyy: { offset: -2, unit: 'weeks', format: 'dd/MM/yyyy' },
  minus2WeeksMmDdYy: { offset: -2, unit: 'weeks', format: 'MM/dd/yy' },
  minus2WeeksMmDdYyyy: { offset: -2, unit: 'weeks', format: 'MM/dd/yyyy' },

  minus1MonthDdMonthYyyy: { offset: -1, unit: 'months', format: 'dd MMMM yyyy' },
  minus1MonthMonthDdYyyy: { offset: -1, unit: 'months', format: 'MMMM dd, yyyy' },
  minus1MonthDdMmYy: { offset: -1, unit: 'months', format: 'dd/MM/yy' },
  minus1MonthDdMmYyyy: { offset: -1, unit: 'months', format: 'dd/MM/yyyy' },
  minus1MonthMmDdYy: { offset: -1, unit: 'months', format: 'MM/dd/yy' },
  minus1MonthMmDdYyyy: { offset: -1, unit: 'months', format: 'MM/dd/yyyy' },

  minus3MonthsDdMonthYyyy: { offset: -3, unit: 'months', format: 'dd MMMM yyyy' },
  minus3MonthsMonthDdYyyy: { offset: -3, unit: 'months', format: 'MMMM dd, yyyy' },
  minus3MonthsDdMmYy: { offset: -3, unit: 'months', format: 'dd/MM/yy' },
  minus3MonthsDdMmYyyy: { offset: -3, unit: 'months', format: 'dd/MM/yyyy' },
  minus3MonthsMmDdYy: { offset: -3, unit: 'months', format: 'MM/dd/yy' },
  minus3MonthsMmDdYyyy: { offset: -3, unit: 'months', format: 'MM/dd/yyyy' },

  minus6MonthsDdMonthYyyy: { offset: -6, unit: 'months', format: 'dd MMMM yyyy' },
  minus6MonthsMonthDdYyyy: { offset: -6, unit: 'months', format: 'MMMM dd, yyyy' },
  minus6MonthsDdMmYy: { offset: -6, unit: 'months', format: 'dd/MM/yy' },
  minus6MonthsDdMmYyyy: { offset: -6, unit: 'months', format: 'dd/MM/yyyy' },
  minus6MonthsMmDdYy: { offset: -6, unit: 'months', format: 'MM/dd/yy' },
  minus6MonthsMmDdYyyy: { offset: -6, unit: 'months', format: 'MM/dd/yyyy' },

  minus1YearDdMonthYyyy: { offset: -1, unit: 'years', format: 'dd MMMM yyyy' },
  minus1YearMonthDdYyyy: { offset: -1, unit: 'years', format: 'MMMM dd, yyyy' },
  minus1YearDdMmYy: { offset: -1, unit: 'years', format: 'dd/MM/yy' },
  minus1YearDdMmYyyy: { offset: -1, unit: 'years', format: 'dd/MM/yyyy' },
  minus1YearMmDdYy: { offset: -1, unit: 'years', format: 'MM/dd/yy' },
  minus1YearMmDdYyyy: { offset: -1, unit: 'years', format: 'MM/dd/yyyy' },

  // Future dates
  tomorrowDdMonthYyyy: { offset: 1, unit: 'days', format: 'dd MMMM yyyy' },
  tomorrowMonthDdYyyy: { offset: 1, unit: 'days', format: 'MMMM dd, yyyy' },
  tomorrowDdMmYy: { offset: 1, unit: 'days', format: 'dd/MM/yy' },
  tomorrowDdMmYyyy: { offset: 1, unit: 'days', format: 'dd/MM/yyyy' },
  tomorrowMmDdYy: { offset: 1, unit: 'days', format: 'MM/dd/yy' },
  tomorrowMmDdYyyy: { offset: 1, unit: 'days', format: 'MM/dd/yyyy' },

  plus1WeekDdMonthYyyy: { offset: 1, unit: 'weeks', format: 'dd MMMM yyyy' },
  plus1WeekMonthDdYyyy: { offset: 1, unit: 'weeks', format: 'MMMM dd, yyyy' },
  plus1WeekDdMmYy: { offset: 1, unit: 'weeks', format: 'dd/MM/yy' },
  plus1WeekDdMmYyyy: { offset: 1, unit: 'weeks', format: 'dd/MM/yyyy' },
  plus1WeekMmDdYy: { offset: 1, unit: 'weeks', format: 'MM/dd/yy' },
  plus1WeekMmDdYyyy: { offset: 1, unit: 'weeks', format: 'MM/dd/yyyy' },

  plus2WeeksDdMonthYyyy: { offset: 2, unit: 'weeks', format: 'dd MMMM yyyy' },
  plus2WeeksMonthDdYyyy: { offset: 2, unit: 'weeks', format: 'MMMM dd, yyyy' },
  plus2WeeksDdMmYy: { offset: 2, unit: 'weeks', format: 'dd/MM/yy' },
  plus2WeeksDdMmYyyy: { offset: 2, unit: 'weeks', format: 'dd/MM/yyyy' },
  plus2WeeksMmDdYy: { offset: 2, unit: 'weeks', format: 'MM/dd/yy' },
  plus2WeeksMmDdYyyy: { offset: 2, unit: 'weeks', format: 'MM/dd/yyyy' },

  plus1MonthDdMonthYyyy: { offset: 1, unit: 'months', format: 'dd MMMM yyyy' },
  plus1MonthMonthDdYyyy: { offset: 1, unit: 'months', format: 'MMMM dd, yyyy' },
  plus1MonthDdMmYy: { offset: 1, unit: 'months', format: 'dd/MM/yy' },
  plus1MonthDdMmYyyy: { offset: 1, unit: 'months', format: 'dd/MM/yyyy' },
  plus1MonthMmDdYy: { offset: 1, unit: 'months', format: 'MM/dd/yy' },
  plus1MonthMmDdYyyy: { offset: 1, unit: 'months', format: 'MM/dd/yyyy' },

  plus3MonthsDdMonthYyyy: { offset: 3, unit: 'months', format: 'dd MMMM yyyy' },
  plus3MonthsMonthDdYyyy: { offset: 3, unit: 'months', format: 'MMMM dd, yyyy' },
  plus3MonthsDdMmYy: { offset: 3, unit: 'months', format: 'dd/MM/yy' },
  plus3MonthsDdMmYyyy: { offset: 3, unit: 'months', format: 'dd/MM/yyyy' },
  plus3MonthsMmDdYy: { offset: 3, unit: 'months', format: 'MM/dd/yy' },
  plus3MonthsMmDdYyyy: { offset: 3, unit: 'months', format: 'MM/dd/yyyy' },

  plus6MonthsDdMonthYyyy: { offset: 6, unit: 'months', format: 'dd MMMM yyyy' },
  plus6MonthsMonthDdYyyy: { offset: 6, unit: 'months', format: 'MMMM dd, yyyy' },
  plus6MonthsDdMmYy: { offset: 6, unit: 'months', format: 'dd/MM/yy' },
  plus6MonthsDdMmYyyy: { offset: 6, unit: 'months', format: 'dd/MM/yyyy' },
  plus6MonthsMmDdYy: { offset: 6, unit: 'months', format: 'MM/dd/yy' },
  plus6MonthsMmDdYyyy: { offset: 6, unit: 'months', format: 'MM/dd/yyyy' },

  plus1YearDdMonthYyyy: { offset: 1, unit: 'years', format: 'dd MMMM yyyy' },
  plus1YearMonthDdYyyy: { offset: 1, unit: 'years', format: 'MMMM dd, yyyy' },
  plus1YearDdMmYy: { offset: 1, unit: 'years', format: 'dd/MM/yy' },
  plus1YearDdMmYyyy: { offset: 1, unit: 'years', format: 'dd/MM/yyyy' },
  plus1YearMmDdYy: { offset: 1, unit: 'years', format: 'MM/dd/yy' },
  plus1YearMmDdYyyy: { offset: 1, unit: 'years', format: 'MM/dd/yyyy' },
};

/**
 * Replaces variable placeholders in text with actual values
 * @param {Object} layer - The layer object containing the text with variables
 * @param {Object} user - The user object with firstName, lastName, email, id, etc.
 * @returns {string} The text with all variables replaced with actual values
 */
export const replaceVariables = (layer, user) => {
  // Helper function to format dates with offset
  const formatDate = (offset, unit, dateFormat) => format(
    add(new Date(), { [unit]: offset }), dateFormat,
  );

  // Build regex pattern from all date variable keys
  const dateVariablePattern = Object.keys(DATE_VARIABLE_CONFIG).join('|');
  const dateVariableRegex = new RegExp(`{{(${dateVariablePattern})}}`, 'g');

  return get(layer, 'extra.meta.value', '')
    // User data variables
    .replace(/{{learnerFullName}}/g, `${user?.firstName || ''} ${user?.lastName || ''}`)
    .replace(/{{learnerFirstName}}/g, user?.firstName || '')
    .replace(/{{learnerLastName}}/g, user?.lastName || '')
    .replace(/{{userId}}/g, user?.id || '')
    .replace(/{{userEmail}}/g, user?.email || '')
    .replace(/{{userInitials}}/g, `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase())
    .replace(/{{userLastFirst}}/g, `${user?.lastName || ''}, ${user?.firstName || ''}`)
    .replace(/{{userLastFirstInitial}}/g, `${user?.lastName || ''}, ${user?.firstName?.[0] || ''}`)
    .replace(/{{userFirstInitialLast}}/g, `${user?.firstName?.[0] || ''} ${user?.lastName || ''}`)

    // Legacy date variable
    .replace(/{{today'sDate}}/g, new Date().toLocaleDateString())

    // Time variables
    .replace(/{{currentTime12}}/g, format(new Date(), 'h:mm a'))
    .replace(/{{currentTime24}}/g, format(new Date(), 'HH:mm'))

    // All date offset variables (past and future) - single replace using dictionary
    .replace(dateVariableRegex, (match, variableName) => {
      const config = DATE_VARIABLE_CONFIG[variableName];

      if (config) {
        return formatDate(config.offset, config.unit, config.format);
      }

      return match; // Fallback if not found
    })

    // Custom date offsets with pattern matching
    .replace(/\{\{customDate:(-?\d+):(days|weeks|months|years):(ddMonthYyyy|monthDdYyyy|ddMmYy|ddMmYyyy|mmDdYy|mmDdYyyy)\}\}/g,
      (match, offset, unit, dateFormat) => {
        try {
          // Map format codes to date-fns format strings
          const formatMap = {
            ddMonthYyyy: 'dd MMMM yyyy',
            monthDdYyyy: 'MMMM dd, yyyy',
            ddMmYy: 'dd/MM/yy',
            ddMmYyyy: 'dd/MM/yyyy',
            mmDdYy: 'MM/dd/yy',
            mmDdYyyy: 'MM/dd/yyyy',
          };

          // Parse and validate offset
          const offsetNum = parseInt(offset, 10);

          if (Math.abs(offsetNum) > 999) {
            console.warn(`Invalid offset ${offsetNum} in custom date variable`);

            return match;
          }

          return formatDate(offsetNum, unit, formatMap[dateFormat]);
        } catch (error) {
          console.error('Error parsing custom date variable:', error);

          return match;
        }
      })

    // New dynamic plus/minus date variables with flexible unit syntax
    // Pattern: {{plus27dDdMonthYyyy}} or {{minus2wMonthDdYyyy}} etc.
    .replace(/\{\{(plus|minus)(\d+)(d|w|m|y)(DdMonthYyyy|MonthDdYyyy|DdMmYy|DdMmYyyy|MmDdYy|MmDdYyyy)\}\}/g,
      (match, operation, offsetValue, unitCode, formatSuffix) => {
        try {
          // Map format suffixes to date-fns format strings
          const formatMap = {
            DdMonthYyyy: 'dd MMMM yyyy',
            MonthDdYyyy: 'MMMM dd, yyyy',
            DdMmYy: 'dd/MM/yy',
            DdMmYyyy: 'dd/MM/yyyy',
            MmDdYy: 'MM/dd/yy',
            MmDdYyyy: 'MM/dd/yyyy',
          };

          // Map unit codes to date-fns unit names
          const unitMap = {
            d: 'days',
            w: 'weeks',
            m: 'months',
            y: 'years',
          };

          // Parse and validate offset
          const offsetNum = parseInt(offsetValue, 10);

          if (offsetNum <= 0 || offsetNum > 999) {
            console.warn(`Invalid offset ${offsetNum} in dynamic date variable`);
            return match;
          }

          // Apply sign based on operation
          const signedOffset = operation === 'minus' ? -offsetNum : offsetNum;

          return formatDate(signedOffset, unitMap[unitCode], formatMap[formatSuffix]);
        } catch {
          console.error('Error parsing dynamic date variable');

          return match;
        }
      });
};
